import { useState } from 'react';
import type { Task, LLMJobType } from '../types';
import { tasksApi, llmApi } from '../services/api';
import { useAppStore } from '../store';
import JobProgressBar from './JobProgressBar';

interface TaskCardProps {
  task: Task;
}

const statusColors: Record<string, string> = {
  not_started: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  blocked: 'bg-red-100 text-red-700',
};

const priorityColors: Record<string, string> = {
  high: 'bg-red-50 text-red-600 border-red-200',
  medium: 'bg-yellow-50 text-yellow-600 border-yellow-200',
  low: 'bg-slate-50 text-slate-600 border-slate-200',
};

const statusLabels: Record<string, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  blocked: 'Blocked',
};

export default function TaskCard({ task }: TaskCardProps) {
  const { updateTask, removeTask, setJobProgress, activeJobs } = useAppStore();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [showAiMenu, setShowAiMenu] = useState(false);
  
  const activeJob = activeJobs.get(task.oceanId || '');
  
  const handleComplete = async () => {
    try {
      const response = await tasksApi.complete(task.id);
      if (response.success && response.data) {
        updateTask(task.id, response.data);
      }
    } catch (error) {
      console.error('Failed to complete task:', error);
    }
  };
  
  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await tasksApi.delete(task.id);
      removeTask(task.id);
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };
  
  const handleAiExecute = async (jobType: LLMJobType) => {
    setShowAiMenu(false);
    setIsExecuting(true);
    
    try {
      const response = await llmApi.execute({
        taskId: task.id,
        jobType,
      });
      
      if (response.success && response.data) {
        // Start polling for job status
        pollJobStatus(response.data.jobId);
      }
    } catch (error) {
      console.error('Failed to execute AI task:', error);
      setIsExecuting(false);
    }
  };
  
  const pollJobStatus = async (jobId: string) => {
    const poll = async () => {
      try {
        const response = await llmApi.getJobStatus(jobId);
        if (response.success && response.data) {
          setJobProgress(jobId, response.data);
          
          if (response.data.status === 'completed') {
            setIsExecuting(false);
            // Refresh task data
            const taskResponse = await tasksApi.get(task.id);
            if (taskResponse.success && taskResponse.data) {
              updateTask(task.id, taskResponse.data);
            }
          } else if (response.data.status === 'failed') {
            setIsExecuting(false);
          } else {
            // Continue polling
            setTimeout(poll, 2000);
          }
        }
      } catch (error) {
        console.error('Failed to poll job status:', error);
        setIsExecuting(false);
      }
    };
    
    poll();
  };
  
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <button
            onClick={handleComplete}
            className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              task.status === 'completed'
                ? 'bg-green-500 border-green-500'
                : 'border-slate-300 hover:border-ocean-500'
            }`}
          >
            {task.status === 'completed' && (
              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3
                className={`font-medium ${
                  task.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-900'
                }`}
              >
                {task.title}
              </h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[task.status]}`}>
                {statusLabels[task.status]}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium border ${priorityColors[task.priority]}`}>
                {task.priority}
              </span>
            </div>
            
            {/* Tags */}
            {task.tags.length > 0 && (
              <div className="flex gap-1 mt-2 flex-wrap">
                {task.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            
            {/* Due date */}
            {task.dueDate && (
              <p className="text-xs text-slate-500 mt-2">
                Due: {new Date(task.dueDate).toLocaleDateString()}
              </p>
            )}
            
            {/* AI Output preview */}
            {task.aiOutput && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-2 text-sm text-ocean-600 hover:text-ocean-700 flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                {isExpanded ? 'Hide AI output' : 'Show AI output'}
              </button>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-1">
            {/* AI Execute button */}
            <div className="relative">
              <button
                onClick={() => setShowAiMenu(!showAiMenu)}
                disabled={isExecuting}
                className="p-2 text-slate-400 hover:text-ocean-600 hover:bg-ocean-50 rounded-lg transition-colors disabled:opacity-50"
                title="AI Actions"
              >
                {isExecuting ? (
                  <div className="w-5 h-5 border-2 border-ocean-500 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                )}
              </button>
              
              {showAiMenu && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10">
                  <button
                    onClick={() => handleAiExecute('research')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                  >
                    <span className="text-purple-500">🔍</span> Research
                  </button>
                  <button
                    onClick={() => handleAiExecute('breakdown')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                  >
                    <span className="text-blue-500">📋</span> Break down
                  </button>
                  <button
                    onClick={() => handleAiExecute('summarize')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                  >
                    <span className="text-green-500">📝</span> Summarize
                  </button>
                </div>
              )}
            </div>
            
            {/* Delete button */}
            <button
              onClick={handleDelete}
              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete task"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Active job progress */}
        {activeJob && activeJob.status === 'running' && (
          <div className="mt-4">
            <JobProgressBar progress={activeJob} />
          </div>
        )}
        
        {/* Expanded AI output */}
        {isExpanded && task.aiOutput && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-medium text-slate-500 uppercase">AI Output</span>
              {task.aiJobType && (
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                  {task.aiJobType}
                </span>
              )}
            </div>
            <div className="prose prose-sm prose-slate max-w-none">
              <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">
                {task.aiOutput}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


